/**
 * Mobile Menu Module
 * Simple left/right sliding navigation system
 */
class MobileMenu {
    constructor() {
        this.isActive = false;
        this.toggleButton = document.querySelector('.mobile-menu-toggle');
        this.overlay = document.querySelector('.mobile-menu-overlay');
        this.menu = document.querySelector('#mobile-menu');
        this.menuNav = document.querySelector('.mobile-menu-nav');
        
        // Simple navigation stack
        this.navigationStack = [];
        this.submenus = [];
        
        this.init();
    }

    init() {
        if (!this.toggleButton || !this.menu) {
            if (this.isDebugEnabled()) {
                console.warn('[MOBILE MENU] Mobile menu elements not found');
            }
            return;
        }

        this.setupSubmenus();
        this.bindEvents();
        if (this.isDebugEnabled()) {
            console.log('[MOBILE MENU] Mobile menu initialized with simple sliding navigation');
        }
    }

    /**
     * Check if debug mode is enabled
     * @returns {boolean} True if debug is enabled
     */
    isDebugEnabled() {
        return typeof sojTheme !== 'undefined' && sojTheme.debug;
    }

    /**
     * Setup submenus for simple sliding navigation
     */
    setupSubmenus() {
        if (!this.menu) {
            if (this.isDebugEnabled()) {
                console.warn('[MOBILE MENU] Menu element not found for submenu setup');
            }
            return;
        }

        // Find all submenus
        this.submenus = this.menu.querySelectorAll('.sub-menu');
        
        if (this.isDebugEnabled()) {
            console.log(`[MOBILE MENU] Found ${this.submenus.length} submenus`);
        }
        
        if (this.submenus.length === 0) {
            if (this.isDebugEnabled()) {
                console.log('[MOBILE MENU] No submenus found');
            }
            return;
        }
        
        this.submenus.forEach((submenu, index) => {
            // Add unique ID if not present
            if (!submenu.id) {
                submenu.id = `submenu-${index}`;
            }
            
            // Set initial state to match CSS
            submenu.style.display = 'none';
            submenu.style.left = '-100vw';
            
            // Create simple back button
            this.createBackButton(submenu);
        });
        
        // Add clickable chevron icons to menu items with children
        this.addChevronIcons();
    }

    /**
     * Add clickable chevron icons to menu items with children
     */
    addChevronIcons() {
        const menuItemsWithChildren = this.menu.querySelectorAll('.menu-item-has-children');
        
        if (this.isDebugEnabled()) {
            console.log(`[MOBILE MENU] Found ${menuItemsWithChildren.length} menu items with children`);
        }
        
        menuItemsWithChildren.forEach((menuItem, index) => {
            const link = menuItem.querySelector('a');
            const submenu = menuItem.querySelector('.sub-menu');
            
            if (this.isDebugEnabled()) {
                console.log(`[MOBILE MENU] Menu item ${index}:`, menuItem);
                console.log(`[MOBILE MENU] Link:`, link);
                console.log(`[MOBILE MENU] Submenu:`, submenu);
            }
            
           
        });
    }

    /**
     * Create simple back button for submenu
     */
    createBackButton(submenu) {
        // Find the parent menu item that contains this submenu
        const parentMenuItem = submenu.closest('.menu-item-has-children');
        const parentTitle = parentMenuItem ? parentMenuItem.querySelector('a').textContent.trim() : 'Back';
        
        // Create a simple back button
        const backButton = document.createElement('button');
        backButton.className = 'back-button';
        backButton.innerHTML = `${parentTitle}`;
        
        // Insert at the beginning of the submenu
        submenu.insertBefore(backButton, submenu.firstChild);
        
        // Add click handler
        backButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.goBack();
        });
    }


    bindEvents() {
        // Toggle button click
        this.toggleButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggle();
        });

        // Overlay click (optional - overlay may be removed)
        if (this.overlay) {
            this.overlay.addEventListener('click', (e) => {
                e.preventDefault();
                this.close();
            });
        }

        // Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isActive) {
                if (this.navigationStack.length > 0) {
                    this.goBack();
                } else {
                    this.close();
                }
            }
        });

        // Close on window resize (if menu is open)
        window.addEventListener('resize', () => {
            if (this.isActive && window.innerWidth > 768) {
                this.close();
            }
        });

        // Submenu navigation
        this.bindSubmenuEvents();
        
        // Close search functionality
        this.bindCloseSearchEvents();
    }

    /**
     * Bind events for submenu navigation
     */
    bindSubmenuEvents() {
        // Fallback: Handle clicks on menu items with children
        // Check if click is on the right side (where chevron would be)
        this.menu.addEventListener('click', (e) => {
            const menuItem = e.target.closest('.menu-item-has-children');
            if (menuItem) {
                const submenu = menuItem.querySelector('.sub-menu');
                if (submenu) {
                    // Check if click is on the right side of the menu item (chevron area)
                    const menuItemRect = menuItem.getBoundingClientRect();
                    const clickX = e.clientX;
                    const chevronArea = menuItemRect.right - 60; // 60px from right edge
                    
                    if (clickX > chevronArea) {
                        // Click is in chevron area - prevent default and show submenu
                        e.preventDefault();
                        e.stopPropagation();
                        
                        if (this.isDebugEnabled()) {
                            console.log('[MOBILE MENU] Click detected in chevron area');
                        }
                        
                        this.navigateToSubmenu(submenu);
                    }
                    // If click is on the link text, let it work normally
                }
            }
        });
    }

    /**
     * Bind events for close search functionality
     */
    bindCloseSearchEvents() {
        // Handle clicks on elements with .close-search class
        document.addEventListener('click', (e) => {
            if (e.target.classList.contains('close-search') || e.target.closest('.close-search')) {
                e.preventDefault();
                
                // Remove 'active' class from mobile-search div
                const mobileSearch = document.querySelector('.mobile-search');
                if (mobileSearch) {
                    mobileSearch.classList.remove('active');
                    
                    if (this.isDebugEnabled()) {
                        console.log('[MOBILE MENU] Removed active class from mobile-search');
                    }
                }
            }
        });
    }

    /**
     * Navigate to a submenu - simple slide right
     */
    navigateToSubmenu(submenu) {
        if (this.isDebugEnabled()) {
            console.log('[MOBILE MENU] navigateToSubmenu called with:', submenu);
        }
        
        // Add to navigation stack
        this.navigationStack.push(submenu);
        
        // Hide main menu when submenu is active
        this.menuNav.classList.add('hidden-by-submenu');
        
        // Show submenu with slide-in animation (matching CSS)
        submenu.style.display = 'flex';
        submenu.style.left = '-100vw';
        submenu.style.visibility = 'visible';
        
        // Force reflow
        submenu.offsetHeight;
        
        // Slide in from right
        submenu.style.left = '0';
        
        if (this.isDebugEnabled()) {
            console.log('[MOBILE MENU] Navigated to submenu, stack length:', this.navigationStack.length);
        }
    }

    /**
     * Go back to previous menu level - simple slide left
     */
    goBack() {
        if (this.navigationStack.length === 0) {
            return;
        }
        
        // Remove current submenu from stack
        const currentSubmenu = this.navigationStack.pop();
        
        // Slide out to right (matching CSS)
        currentSubmenu.style.left = '-100vw';
        currentSubmenu.style.visibility = 'hidden';
        
        // Hide submenu after animation
        setTimeout(() => {
            currentSubmenu.style.display = 'none';
            currentSubmenu.style.left = '-100vw';
        }, 400); // Match CSS transition duration
        
        // Show main menu if back to root level
        if (this.navigationStack.length === 0) {
            this.menuNav.classList.remove('hidden-by-submenu');
        }
        
        if (this.isDebugEnabled()) {
            console.log('[MOBILE MENU] Went back to previous level');
        }
    }


    toggle() {
        if (this.isActive) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        document.body.classList.add('mobile-menu-active');
        this.isActive = true;
        
        if (this.menu) {
            this.menu.removeAttribute('hidden');
            this.menu.setAttribute('aria-hidden', 'false');
        }
        
        document.dispatchEvent(new CustomEvent('mobileMenuOpen'));
        
        if (this.isDebugEnabled()) {
            console.log('[MOBILE MENU] Mobile menu opened');
        }
    }

    close() {
        this.resetNavigationState();
        document.body.classList.remove('mobile-menu-active');
        this.isActive = false;
        
        if (this.menu) {
            this.menu.setAttribute('hidden', '');
            this.menu.setAttribute('aria-hidden', 'true');
        }
        
        document.dispatchEvent(new CustomEvent('mobileMenuClose'));
        
        if (this.isDebugEnabled()) {
            console.log('[MOBILE MENU] Mobile menu closed');
        }
    }

    /**
     * Reset navigation state when closing menu
     */
    resetNavigationState() {
        // Hide all submenus (matching CSS)
        this.submenus.forEach(submenu => {
            submenu.style.display = 'none';
            submenu.style.left = '-100vw';
            submenu.style.visibility = 'hidden';
        });
        
        // Show main menu
        this.menuNav.classList.remove('hidden-by-submenu');
        
        // Reset navigation stack
        this.navigationStack = [];
        
        if (this.isDebugEnabled()) {
            console.log('[MOBILE MENU] Navigation state reset');
        }
    }
}

export default MobileMenu;
